// src/pages/DraftPage.tsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Home, 
  MapPin, 
  DollarSign, 
  Bed, 
  Bath, 
  Square,
  Clock,
  Trash2,
  ArrowRight,
  ArrowLeft,
  FileText,
  AlertCircle,
  Loader2,
  Mail,
  Layout,
  Users,
  Calendar,
  Edit3,
  CheckCircle,
  Image as ImageIcon,
  Tag,
  Building,
  RefreshCw,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import campaignDraftService, { CampaignDraftRecord } from '@/services/campaignDraftService';
import { DraftProperty } from '@/services/draftService';

const DraftPage: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();

  type DisplayDraft = DraftProperty & CampaignDraftRecord;
  const [draft, setDraft] = useState<DisplayDraft | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  // Load draft on mount
  useEffect(() => {
    loadDraft();
  }, []);

  const normalizeDraft = (item: CampaignDraftRecord | DraftProperty): DisplayDraft => {
    const raw = item as Record<string, any>;
    const timeSinceUpdate = raw.time_since_update || (raw.updated ? formatTimeSince(raw.updated) : undefined);

    return {
      ...item,
      title: raw.title || raw.campaign_name || raw.headline || undefined,
      description: raw.description || raw.message || undefined,
      subject: raw.subject || raw.campaign_subject || undefined,
      image_url: raw.image_url || raw.image || raw.hero_image || undefined,
      time_since_update: timeSinceUpdate,
      created: raw.created,
      updated: raw.updated,
    } as DisplayDraft;
  };

  const loadDraft = async () => {
    try {
      setIsLoading(true);
      const drafts = await campaignDraftService.listDrafts();
      const selectedDraft = drafts.find((item) => String(item.campaign_type || '').toLowerCase() === 'property') || drafts[0] || null;
      setDraft(selectedDraft ? normalizeDraft(selectedDraft) : null);
    } catch (error) {
      console.error('Failed to load draft:', error);
      toast({
        title: 'Error',
        description: 'Failed to load draft data.',
        variant: 'destructive',
        duration:1000
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleContinueDraft = () => {
    if (!draft) return;

    // Store draft data in sessionStorage for the campaign page to pick up
    try {
      sessionStorage.setItem('draftToRestore', JSON.stringify(draft));
      sessionStorage.setItem('restoreDraftOnLoad', 'true');
    } catch (e) {
      console.warn('Could not save to sessionStorage');
    }

    // Navigate to campaign creation page
    navigate('/builder');
  };

  const handleDeleteDraft = async () => {
    try {
      if (!draft?.id) {
        throw new Error('Draft not found');
      }
      setIsDeleting(true);
      await campaignDraftService.deleteDraft(draft.id);
      setDraft(null);
      setShowDeleteDialog(false);

      // Clear sessionStorage
      sessionStorage.removeItem('selectedTemplateId');
      sessionStorage.removeItem('campaignDraftState');
      sessionStorage.removeItem('selectedPropertyId');
      sessionStorage.removeItem('draftToRestore');

      toast({
        title: 'Draft Deleted',
        description: 'Your draft has been deleted successfully.',
        duration:1000
      });
    } catch (error) {
      console.error('Failed to delete draft:', error);
      toast({
        title: 'Error',
        description: 'Failed to delete draft. Please try again.',
        variant: 'destructive',
        duration:1000
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const formatPrice = (price: number | null) => {
    if (!price) return 'Not set';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      maximumFractionDigits: 0,
    }).format(price);
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      });
    } catch {
      return dateString;
    }
  };

  const formatTimeSince = (dateString: string) => {
    try {
      const date = new Date(dateString);
      const diffMs = Date.now() - date.getTime();
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      if (diffDays > 0) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
      const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
      if (diffHours > 0) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
      const diffMinutes = Math.floor(diffMs / (1000 * 60));
      return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
    } catch {
      return dateString;
    }
  };

  // Loading State
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-12 w-12 animate-spin text-blue-600 mx-auto mb-4" />
          <p className="text-gray-600 text-lg">Loading your draft...</p>
        </div>
      </div>
    );
  }

  // No Draft State
  if (!draft) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-slate-100">
        <div className="max-w-2xl mx-auto px-4 py-16">
          {/* Back Button */}
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-8"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go Back
          </Button>

          <Card className="text-center py-16">
            <CardContent>
              <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-gray-100 flex items-center justify-center">
                <FileText className="h-10 w-10 text-gray-400" />
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-3">No Draft Found</h2>
              <p className="text-gray-600 mb-8 max-w-md mx-auto">
                You don't have any saved drafts. Start creating a new campaign to save your progress.
              </p>
              <Button
                onClick={() => navigate('/builder')}
                className="bg-blue-600 hover:bg-blue-700"
              >
                Create New Campaign
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Draft Found - Show Details
  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-slate-100">
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Go Back
          </Button>

          <Button
            variant="outline"
            onClick={loadDraft}
            size="sm"
          >
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Page Title */}
        <div className="text-center mb-10">
          <div className="inline-flex items-center gap-2 bg-amber-100 text-amber-800 px-4 py-2 rounded-full text-sm font-medium mb-4">
            <FileText className="h-4 w-4" />
            Saved Draft
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Your Launch Draft
          </h1>
          <p className="text-gray-600">
            Review your saved progress and continue where you left off
          </p>
        </div>

        {/* Main Draft Card */}
        <Card className="mb-6 overflow-hidden shadow-lg">
          {/* Hero Image Section */}
          <div className="relative h-64 bg-gradient-to-br from-blue-100 to-indigo-100">
            {draft.image_url ? (
              <img
                src={draft.image_url}
                alt={draft.title || 'Property'}
                className="w-full h-full object-cover"
              />
            ) : (
              <div className="w-full h-full flex flex-col items-center justify-center">
                <ImageIcon className="h-16 w-16 text-blue-300 mb-3" />
                <p className="text-blue-400 font-medium">No image uploaded</p>
              </div>
            )}
            
            {/* Overlay Badge */}
            <div className="absolute top-4 left-4">
              <Badge className="bg-amber-500 text-white text-sm px-3 py-1">
                Draft
              </Badge>
            </div>

            {/* Time Badge */}
            <div className="absolute top-4 right-4">
              <div className="bg-black/50 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-sm flex items-center gap-2">
                <Clock className="h-4 w-4" />
                {draft.time_since_update}
              </div>
            </div>
          </div>

          <CardContent className="p-6">
            {/* Title & Address */}
            <div className="mb-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">
                {draft.title || draft.address || 'Untitled Property'}
              </h2>
              
              {(draft.address || draft.city || draft.state) && (
                <p className="text-gray-600 flex items-center gap-2">
                  <MapPin className="h-5 w-5 text-gray-400" />
                  {[draft.address, draft.city, draft.state, draft.postal_code]
                    .filter(Boolean)
                    .join(', ')}
                </p>
              )}
            </div>

            {/* Price */}
            {draft.price && draft.price > 0 && (
              <div className="mb-6">
                <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-lg">
                  <DollarSign className="h-5 w-5" />
                  <span className="text-2xl font-bold">{formatPrice(draft.price)}</span>
                </div>
              </div>
            )}

            {/* Property Details Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
              {draft.bedrooms !== null && draft.bedrooms > 0 && (
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <Bed className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gray-900">{draft.bedrooms}</p>
                  <p className="text-sm text-gray-600">Bedrooms</p>
                </div>
              )}
              
              {draft.bathrooms !== null && draft.bathrooms > 0 && (
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <Bath className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gray-900">{draft.bathrooms}</p>
                  <p className="text-sm text-gray-600">Bathrooms</p>
                </div>
              )}
              
              {draft.area !== null && draft.area > 0 && (
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <Square className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                  <p className="text-2xl font-bold text-gray-900">{draft.area.toLocaleString()}</p>
                  <p className="text-sm text-gray-600">Sq Ft</p>
                </div>
              )}
              
              {draft.property_type && (
                <div className="bg-gray-50 rounded-lg p-4 text-center">
                  <Building className="h-6 w-6 text-blue-600 mx-auto mb-2" />
                  <p className="text-lg font-bold text-gray-900 truncate">{draft.property_type}</p>
                  <p className="text-sm text-gray-600">Type</p>
                </div>
              )}
            </div>

            {/* Description */}
            {draft.description && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-2">
                  Description
                </h3>
                <p className="text-gray-700 leading-relaxed">
                  {draft.description}
                </p>
              </div>
            )}

            {/* Additional Photos */}
            {draft.photos && draft.photos.length > 0 && (
              <div className="mb-6">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider mb-3">
                  Additional Images ({draft.photos.length})
                </h3>
                <div className="grid grid-cols-4 gap-2">
                  {draft.photos.slice(0, 4).map((photo, index) => (
                    <div key={index} className="aspect-square rounded-lg overflow-hidden bg-gray-100">
                      <img
                        src={photo}
                        alt={`Photo ${index + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ))}
                  {draft.photos.length > 4 && (
                    <div className="aspect-square rounded-lg bg-gray-100 flex items-center justify-center">
                      <span className="text-gray-600 font-medium">
                        +{draft.photos.length - 4} more
                      </span>
                    </div>
                  )}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Campaign Details Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Tag className="h-5 w-5 text-blue-600" />
              Campaign Details
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Email Subject */}
            {draft.subject && (
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <Mail className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-500">Email Subject</p>
                  <p className="text-gray-900">{draft.subject}</p>
                </div>
              </div>
            )}

            {/* Property List Type */}
            {draft.property_list_name && (
              <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                <Layout className="h-5 w-5 text-gray-400 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-gray-500">List Type</p>
                  <p className="text-gray-900">{draft.property_list_name}</p>
                </div>
              </div>
            )}

            {/* Open House */}
            {draft.is_open_house && (
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg">
                <CheckCircle className="h-5 w-5 text-blue-600" />
                <p className="text-blue-900 font-medium">Open House Enabled</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Timestamps Card */}
        <Card className="mb-8">
          <CardContent className="py-4">
            <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-gray-600">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-gray-400" />
                <span>Created: {formatDate(draft.created)}</span>
              </div>
              <div className="w-px h-4 bg-gray-300 hidden sm:block" />
              <div className="flex items-center gap-2">
                <Clock className="h-4 w-4 text-gray-400" />
                <span>Last updated: {formatDate(draft.updated)}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <Button
            variant="outline"
            size="lg"
            onClick={() => setShowDeleteDialog(true)}
            className="w-full sm:w-auto text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
          >
            <Trash2 className="h-5 w-5 mr-2" />
            Delete Draft
          </Button>

          <Button
            size="lg"
            onClick={handleContinueDraft}
            className="w-full sm:w-auto bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg hover:shadow-xl transition-all"
          >
            <Edit3 className="h-5 w-5 mr-2" />
            Continue Editing
            <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
        </div>

        {/* Info Box */}
        <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-xl">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-blue-800 font-medium mb-1">
                Your progress is saved
              </p>
              <p className="text-sm text-blue-700">
                When you continue editing, you'll pick up right where you left off. 
                Your property details, template selection, and preferences will be restored.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <AlertCircle className="h-5 w-5 text-red-500" />
              Delete Draft?
            </AlertDialogTitle>
            <AlertDialogDescription>
              This will permanently delete your saved draft including all property details, 
              template selections, and campaign preferences. This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          {/* Draft Preview in Delete Dialog */}
          <div className="my-4 p-4 bg-gray-50 rounded-lg border">
            <div className="flex items-center gap-3">
              {draft.image_url ? (
                <img
                  src={draft.image_url}
                  alt="Property"
                  className="w-16 h-16 rounded-lg object-cover"
                />
              ) : (
                <div className="w-16 h-16 rounded-lg bg-gray-200 flex items-center justify-center">
                  <Home className="h-8 w-8 text-gray-400" />
                </div>
              )}
              <div>
                <p className="font-medium text-gray-900">
                  {draft.title || draft.address || 'Untitled Property'}
                </p>
                <p className="text-sm text-gray-500">
                  Last updated: {draft.time_since_update}
                </p>
              </div>
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteDraft}
              disabled={isDeleting}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              {isDeleting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-4 w-4 mr-2" />
                  Delete Draft
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default DraftPage;